// The races on one event, each with the prefix of its live-tracking address.
//
// Configured here, on the Event dialog, because it's a handful of rows per event
// rather than something needing a dialog of its own — and read by the incident
// dialog to offer "Open tracking" for the patient (see tracking.js). Rows save as
// you go, like the event's information links, so there is nothing here to Save.
//
// The race NAME is the join: it is matched against the incident's race, which was
// copied from the imported entry list. So the name field offers the entry list's
// own race names — a typo there is a link that silently never appears.
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Box, Paper, Stack, Typography, Button, IconButton, Tooltip, TextField,
  Autocomplete, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { API_BASE_URL } from "../config";
import { notify } from "../notify";
import { openTracking } from "../tracking";

const RACES_API = `${API_BASE_URL}/event-races`;
const PARTICIPANTS_API = `${API_BASE_URL}/participants`;

const BLANK = { name: "", tracking_url_prefix: "" };

// A number to append when testing a prefix from here — any competitor's would do;
// the point is to see that the address resolves.
const SAMPLE_NUMBER = "1";

/**
 * Props: eventId (an id, or anything else while the parent dialog closes),
 * canEdit — writes need Events.edit, the same as the event's own fields;
 * onSummary(text) — a one-line description for a collapsed section.
 */
export default function EventRaceManager({ eventId, canEdit, onSummary }) {
  const [races, setRaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [raceNames, setRaceNames] = useState([]); // from the entry list, as suggestions
  const [editing, setEditing] = useState(null);   // null | "new" | id
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // The Events dialog passes its `editing` state, which drops to null while the
  // dialog fades out but stays mounted — asking for races without an event is a
  // 422, so wait for an id (the same guard LinkManager uses).
  const identified = typeof eventId === "number";

  const load = useCallback(() => {
    if (!identified) { setRaces([]); setLoading(true); return Promise.resolve(); }
    return axios.get(RACES_API + "/", { params: { event_id: eventId } })
      .then(res => setRaces(res.data || []))
      .finally(() => setLoading(false));
  }, [identified, eventId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (loading) return;
    onSummary?.(races.length
      ? `${races.length} race${races.length === 1 ? "" : "s"}: ${races.map(r => r.name).join(", ")}`
      : "No races configured");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [races, loading]);

  // The distinct race names on the entry list, offered when adding a race so the
  // configured name matches what incidents will carry. Best-effort: whoever edits
  // an event normally holds EventParticipants.view, but if they don't, the field
  // is still free text.
  useEffect(() => {
    if (!identified) { setRaceNames([]); return; }
    axios.get(PARTICIPANTS_API + "/", {
      params: { event_id: eventId }, suppressErrorToast: true,
    })
      .then(res => setRaceNames(
        [...new Set((res.data || []).map(p => (p.race || "").trim()).filter(Boolean))].sort()))
      .catch(() => setRaceNames([]));
  }, [identified, eventId]);

  const openNew = () => { setForm(BLANK); setEditing("new"); };

  const openEdit = (race) => {
    if (!canEdit) return;
    setForm({ name: race.name || "", tracking_url_prefix: race.tracking_url_prefix || "" });
    setEditing(race.id);
  };

  const close = () => { setEditing(null); setForm(BLANK); };

  const save = () => {
    const payload = {
      name: form.name.trim(),
      tracking_url_prefix: form.tracking_url_prefix.trim(),
    };
    const req = editing === "new"
      ? axios.post(RACES_API + "/", { ...payload, event_id: eventId })
      : axios.put(`${RACES_API}/${editing}`, payload);
    setBusy(true);
    req.then(() => {
      notify(editing === "new" ? "Race added." : "Race saved.", "success");
      load();
      close();
    }).catch(() => {}) // the global interceptor surfaces the error
      .finally(() => setBusy(false));
  };

  const doDelete = () => {
    setBusy(true);
    axios.delete(`${RACES_API}/${confirmDelete.id}`)
      .then(() => {
        notify("Race removed.", "success");
        setConfirmDelete(null);
        if (editing === confirmDelete.id) close();
        load();
      })
      .catch(() => {})
      .finally(() => setBusy(false));
  };

  const prefix = form.tracking_url_prefix.trim();
  const prefixOk = /^https?:\/\//i.test(prefix);
  const canSave = !!form.name.trim() && prefixOk && !busy;

  return (
    <Box>
      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "action.hover" } }}>
                <TableCell>Race</TableCell>
                <TableCell>Tracking link prefix</TableCell>
                {canEdit && <TableCell align="right" padding="checkbox" />}
              </TableRow>
            </TableHead>
            <TableBody>
              {races.map(race => (
                <TableRow hover key={race.id} onClick={() => openEdit(race)}
                          sx={{ cursor: canEdit ? "pointer" : "default" }}>
                  <TableCell>{race.name}</TableCell>
                  <TableCell sx={{ wordBreak: "break-all" }}>
                    {race.tracking_url_prefix}
                  </TableCell>
                  {canEdit && (
                    <TableCell align="right" padding="checkbox">
                      <Tooltip title="Remove">
                        <IconButton size="small" color="error"
                                    aria-label={`Remove ${race.name}`}
                                    onClick={(e) => { e.stopPropagation(); setConfirmDelete(race); }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {races.length === 0 && (
                <TableRow>
                  <TableCell colSpan={canEdit ? 3 : 2} align="center"
                             sx={{ color: "text.secondary", py: 2 }}>
                    {loading ? <CircularProgress size={24} />
                             : "No races configured — incidents won't offer a tracking link."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {canEdit && (
        <Button size="small" startIcon={<AddIcon />} onClick={openNew} sx={{ mt: 1 }}>
          Add race
        </Button>
      )}

      {/* Add / edit one race */}
      <Dialog open={editing !== null} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle>{editing === "new" ? "Add race" : "Edit race"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              freeSolo
              options={raceNames}
              value={form.name}
              onChange={(_, v) => setForm(f => ({ ...f, name: v || "" }))}
              onInputChange={(_, v) => setForm(f => ({ ...f, name: v }))}
              renderInput={(params) => (
                <TextField {...params} label="Race name" required
                           helperText={raceNames.length
                             ? "Must match the race on the entry list — pick one from the list."
                             : 'As it appears on the entry list, e.g. "100 mile".'} />
              )}
            />
            <TextField label="Tracking link prefix" fullWidth required
                       value={form.tracking_url_prefix}
                       onChange={(e) => setForm(f => ({ ...f, tracking_url_prefix: e.target.value }))}
                       placeholder="https://live.opentracking.co.uk/26Dragon30/?b="
                       error={!!prefix && !prefixOk}
                       helperText={prefix && !prefixOk
                         ? "Must start with http:// or https://"
                         : "The competitor's race number is added to the end of this."} />
            {prefixOk && (
              <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                <Button size="small" startIcon={<OpenInNewIcon />}
                        onClick={() => openTracking(prefix + SAMPLE_NUMBER)}>
                  Test
                </Button>
                <Typography variant="caption" color="text.secondary"
                            sx={{ wordBreak: "break-all" }}>
                  {prefix}{SAMPLE_NUMBER}
                </Typography>
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={busy}>Cancel</Button>
          <Button variant="contained" disabled={!canSave} onClick={save}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={confirmDelete !== null} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Remove race?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Incidents for "{confirmDelete?.name}" will stop offering a tracking link.
            Nothing else changes — no participant or incident refers to this entry.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} disabled={busy}>Cancel</Button>
          <Button color="error" variant="contained" startIcon={<DeleteIcon />}
                  onClick={doDelete} disabled={busy}>Remove</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
